/**
 * Doc Pack catalog route
 *
 * BUG-2026-05-05-03 fix: cards use flex column + CTA pinned bottom
 * (`marginTop: auto`) so all "Xem chi tiết" buttons share one
 * horizontal baseline regardless of body copy length.
 *
 * BUG-2026-05-05-02 fix: render single-locale card text + CTA via
 * useLang() so VN/EN flip is reflected.
 */

import { useEffect, useState } from 'react';
import {
  DOCPACK_CATALOG,
  pricingFor,
  type Currency,
} from '@core/docpack-fe-helpers';
import { detectCurrency } from '@core/landings-helpers';
import { useLang } from '../use-lang';

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function pickInitialCurrency(): Currency {
  const stored = readStorage('asklaw_currency');
  const resolution = detectCurrency({
    storage_value: stored === 'VND' || stored === 'USD' ? stored : null,
    accept_language: navigator.language,
  });
  if (resolution.kind === 'resolved') return resolution.currency;
  return resolution.suggested;
}

export function DocPackCatalogRoute() {
  const [currency, setCurrency] = useState<Currency>('VND');
  const { lang } = useLang();
  useEffect(() => {
    setCurrency(pickInitialCurrency());
  }, []);

  const t = {
    heading: lang === 'vi' ? 'Doc Pack' : 'Doc Pack',
    intro: lang === 'vi'
      ? `Bộ tài liệu PDF chuyên sâu - từ ${currency === 'VND' ? '199.000 đ' : '$19'}.`
      : `In-depth PDF document packs - from ${currency === 'VND' ? '199,000 VND' : '$19'}.`,
    pages: lang === 'vi' ? 'trang' : 'pages',
    sections: lang === 'vi' ? 'mục' : 'sections',
    cta: lang === 'vi' ? 'Xem chi tiết' : 'View details',
    currencyLabel: lang === 'vi' ? 'Chọn tiền tệ' : 'Select currency',
  };

  return (
    <main>
      <h1>{t.heading}</h1>
      <p style={{ color: 'var(--color-muted)' }}>{t.intro}</p>
      <CurrencyToggle current={currency} onChange={setCurrency} ariaLabel={t.currencyLabel} />
      <div className="catalog-grid" style={{ marginTop: 16 }}>
        {DOCPACK_CATALOG.map((sku) => {
          const p = pricingFor(sku, currency);
          const title = lang === 'vi' ? sku.title_vn : sku.title_en;
          const desc = lang === 'vi' ? sku.description_vn : sku.description_en;
          return (
            <article
              key={sku.sku}
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 20,
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                background: 'var(--color-card)',
                height: '100%',
              }}
            >
              <h3 style={{ marginTop: 0 }}>{title}</h3>
              <p style={{ color: 'var(--color-muted)' }}>{desc}</p>
              <small>
                {sku.est_pages} {t.pages} - {sku.section_count} {t.sections}
              </small>
              <div style={{ marginTop: 12, fontSize: 20, fontWeight: 600 }}>{p.display}</div>
              <a
                className="cta-primary"
                href={`/docpack/${sku.sku}`}
                style={{ marginTop: 'auto', alignSelf: 'flex-start' }}
              >
                {t.cta}
              </a>
            </article>
          );
        })}
      </div>
    </main>
  );
}

function CurrencyToggle({
  current,
  onChange,
  ariaLabel,
}: {
  current: Currency;
  onChange: (c: Currency) => void;
  ariaLabel: string;
}) {
  function flip(c: Currency) {
    try {
      window.localStorage.setItem('asklaw_currency', c);
      document.cookie = `asklaw_currency=${c}; Path=/; Max-Age=31536000; SameSite=Lax`;
    } catch {
      // ignore
    }
    onChange(c);
  }
  return (
    <div role="radiogroup" aria-label={ariaLabel} style={{ display: 'flex', gap: 8 }}>
      {(['VND', 'USD'] as Currency[]).map((c) => (
        <button
          key={c}
          onClick={() => flip(c)}
          aria-pressed={current === c}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            background: current === c ? 'var(--color-accent)' : 'transparent',
            color: current === c ? 'var(--color-accent-fg)' : 'inherit',
          }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}
